import React from 'react';
import { Autocomplete, Box, Button, MenuItem, Popper, Select, TextField, Typography } from '@mui/material';
import { LANGUAGES } from '../../components/FindTalentsContainer/filterEnums';
import { INFLUENCER_CATEGORY_MAIN } from '../../constants/influencerCategories';
import { countries } from '../../utils/countriesList';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY } from './brandDashboardTokens';
import type { InfluencerSearchFilters, PlatformFilter } from './brandInfluencerSearch';
import { formatLanguageForApi } from './brandInfluencerSearch';

/** Matches MUI `Select` size="small" height in this popover. */
const FILTER_FIELD_HEIGHT = 40;

const filterOutlinedFieldSx = {
    bgcolor: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
    fontSize: 13,
    color: '#fff',
    minHeight: FILTER_FIELD_HEIGHT,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(255,201,101,0.4)' },
};

const filterInputSx = {
    '& .MuiOutlinedInput-root': filterOutlinedFieldSx,
    '& .MuiInputBase-input': {
        py: 0,
        fontSize: 13,
        '&::placeholder': { color: 'rgba(255,255,255,0.45)', opacity: 1 },
    },
};

const filterAutocompleteSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        ...filterOutlinedFieldSx,
        py: '0 !important',
        px: '9px !important',
        alignItems: 'center',
        flexWrap: 'nowrap',
    },
    '& .MuiAutocomplete-input': {
        py: '0 !important',
        minWidth: 48,
        fontSize: 13,
    },
    '& .MuiChip-root': {
        height: 24,
        maxWidth: '100%',
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 12,
        bgcolor: 'rgba(255, 201, 101, 0.12)',
        color: '#ffc965',
        border: '1px solid rgba(255, 201, 101, 0.28)',
        '& .MuiChip-label': { px: 1 },
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255,255,255,0.55)',
            fontSize: 16,
            '&:hover': { color: '#fff' },
        },
    },
    '& .MuiAutocomplete-endAdornment': {
        position: 'absolute',
        top: '50%',
        right: 8,
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        '& .MuiButtonBase-root': {
            color: 'rgba(255,255,255,0.4)',
            p: 0.25,
        },
        '& .MuiAutocomplete-popupIndicator': {
            color: 'rgba(255,255,255,0.4)',
        },
        '& .MuiAutocomplete-clearIndicator': {
            color: 'rgba(255,255,255,0.4)',
        },
    },
};

const filterMenuProps = {
    PaperProps: {
        sx: {
            maxHeight: 280,
            bgcolor: '#1a1919',
            border: '1px solid rgba(255,255,255,0.07)',
            '& .MuiMenuItem-root': {
                fontSize: 13,
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                '&.Mui-selected': { bgcolor: 'rgba(255,201,101,0.1)' },
            },
        },
    },
};

const filterSelectSx = {
    width: '100%',
    bgcolor: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
    fontSize: 13,
    color: '#fff',
    minHeight: FILTER_FIELD_HEIGHT,
    '& .MuiSelect-select': {
        color: '#fff',
        minHeight: '24px !important',
        display: 'flex',
        alignItems: 'center',
        py: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,201,101,0.4)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
};

const autocompletePopper = (props: React.ComponentProps<typeof Popper>) => (
    <Popper
        {...props}
        sx={{
            '& .MuiPaper-root': {
                bgcolor: '#1a1919',
                border: '1px solid rgba(255,255,255,0.07)',
            },
            '& .MuiAutocomplete-option': {
                fontSize: 13,
                color: '#fff',
            },
        }}
    />
);

const LANGUAGE_OPTIONS = [...LANGUAGES] as string[];

function renderFilterAutocompleteInput(placeholder: string) {
    return (params: Parameters<NonNullable<React.ComponentProps<typeof Autocomplete>['renderInput']>>[0]) => (
        <TextField
            {...params}
            placeholder={placeholder}
            sx={filterInputSx}
            InputProps={{
                ...params.InputProps,
                notched: false,
            }}
        />
    );
}

function CountrySearchAutocomplete({
    placeholder,
    value,
    onChange,
}: {
    placeholder: string;
    value: string;
    onChange: (countryName: string) => void;
}) {
    const selected = countries.find(c => c.name === value) ?? null;

    return (
        <Autocomplete
            size="small"
            options={countries}
            value={selected}
            onChange={(_, option) => onChange(option?.name ?? '')}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter(
                    opt =>
                        opt.name.toLowerCase().includes(q) ||
                        opt.code.toLowerCase().includes(q) ||
                        opt.dial_code.includes(q),
                );
            }}
            noOptionsText="No countries found"
            PopperComponent={autocompletePopper}
            sx={filterAutocompleteSx}
            renderOption={(props, option) => (
                <Box
                    component="li"
                    {...props}
                    key={option.code}
                    sx={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span">{option.flag}</Box>
                    {option.name}
                </Box>
            )}
            renderInput={renderFilterAutocompleteInput(placeholder)}
        />
    );
}

export interface BrandInfluencerFiltersPopoverProps {
    draft: InfluencerSearchFilters;
    onDraftChange: (patch: Partial<InfluencerSearchFilters>) => void;
    onApply: () => void;
    onClear: () => void;
}

export function BrandInfluencerFiltersPopover({
    draft,
    onDraftChange,
    onApply,
    onClear,
}: BrandInfluencerFiltersPopoverProps) {
    return (
        <>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff', mb: 2 }}>
                Filters
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <CountrySearchAutocomplete
                    placeholder="Any country"
                    value={draft.country}
                    onChange={country => onDraftChange({ country })}
                />

                <CountrySearchAutocomplete
                    placeholder="Any nationality"
                    value={draft.nationality}
                    onChange={nationality => onDraftChange({ nationality })}
                />

                <Autocomplete
                    size="small"
                    options={[...INFLUENCER_CATEGORY_MAIN]}
                    value={draft.category || null}
                    onChange={(_, value) => onDraftChange({ category: value ?? '' })}
                    PopperComponent={autocompletePopper}
                    sx={filterAutocompleteSx}
                    renderInput={renderFilterAutocompleteInput('Category / niche')}
                />

                <Autocomplete
                    multiple
                    size="small"
                    options={LANGUAGE_OPTIONS}
                    value={draft.languages}
                    onChange={(_, value) => onDraftChange({ languages: value })}
                    getOptionLabel={formatLanguageForApi}
                    isOptionEqualToValue={(a, b) => a === b}
                    limitTags={1}
                    disableCloseOnSelect
                    PopperComponent={autocompletePopper}
                    sx={filterAutocompleteSx}
                    renderInput={renderFilterAutocompleteInput(draft.languages.length === 0 ? 'Languages spoken' : '')}
                />

                <Select
                    size="small"
                    displayEmpty
                    value={draft.platform}
                    onChange={e => onDraftChange({ platform: e.target.value as PlatformFilter })}
                    sx={filterSelectSx}
                    MenuProps={filterMenuProps}>
                    <MenuItem value="all">All platforms</MenuItem>
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="tiktok">TikTok</MenuItem>
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="twitter">Twitter / X</MenuItem>
                </Select>

                <Select
                    size="small"
                    displayEmpty
                    value={draft.gender}
                    onChange={e => onDraftChange({ gender: e.target.value })}
                    sx={filterSelectSx}
                    MenuProps={filterMenuProps}>
                    <MenuItem value="">Any gender</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="non-binary">Non-binary</MenuItem>
                </Select>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Min followers"
                        value={draft.minFollowers}
                        onChange={e => onDraftChange({ minFollowers: e.target.value.replace(/\D/g, '') })}
                        sx={filterInputSx}
                    />
                    <TextField
                        size="small"
                        placeholder="Max followers"
                        value={draft.maxFollowers}
                        onChange={e => onDraftChange({ maxFollowers: e.target.value.replace(/\D/g, '') })}
                        sx={filterInputSx}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Min engagement %"
                        value={draft.minEngagementRate}
                        onChange={e =>
                            onDraftChange({
                                minEngagementRate: e.target.value.replace(/[^\d.]/g, ''),
                            })
                        }
                        sx={filterInputSx}
                    />
                    <TextField
                        size="small"
                        placeholder="Max engagement %"
                        value={draft.maxEngagementRate}
                        onChange={e =>
                            onDraftChange({
                                maxEngagementRate: e.target.value.replace(/[^\d.]/g, ''),
                            })
                        }
                        sx={filterInputSx}
                    />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
                <Button
                    fullWidth
                    size="small"
                    onClick={onClear}
                    sx={{
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: '#adaaaa',
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    }}>
                    Clear
                </Button>
                <Button
                    fullWidth
                    size="small"
                    onClick={onApply}
                    sx={{
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: '#392700',
                        bgcolor: BRAND_LUXE_PRIMARY,
                        '&:hover': { bgcolor: '#feb700' },
                    }}>
                    Apply
                </Button>
            </Box>
        </>
    );
}
